<template>
  <PageView v-if="page_module" :page-reference="PAGE_REFERENCES.HOMEPAGE" :current-object-type="default_object_type_obj">
    <template #header>
      <TripleMenu
        v-if="$route.params?.page"
        :set-edit-mode="setEditMode"
        :edit-mode-active="edit_mode_active"
        :quick-actions="top_right_menu_quick_actions"
        :components-actions="top_right_menu_components_actions"
        :current-template="current_template"
        :page-reference="PAGE_REFERENCES.HOMEPAGE" />
    </template>
    <template #body>
      <LayoutContainer
        key="home"
        v-model:current-template="current_template"
        :set-edit-mode="setEditMode"
        :edit-mode-active="edit_mode_active"
        :page-reference="PAGE_REFERENCES.HOMEPAGE" />
    </template>
  </PageView>
  <Modal v-model:is-open="show_confirm_unsave_layout">
    <div class="sm:flex sm:items-start">
      <div class="flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto bg-red-100 rounded-full sm:mx-0 sm:h-10 sm:w-10">
        <CircleHelp class="w-8 h-8 text-red-600" aria-hidden="true" />
      </div>
      <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
        <div as="h3" class="text-lg font-medium leading-6 text-gray-900">
          {{ $t("Unsaved Layout Changes") }}
        </div>
        <div class="flex flex-col mt-2">
          <p class="font-semibold text-md">
            {{ $t("Do you really want to exit this page? Any unsaved changes will be disregarded.") }}
          </p>
        </div>
      </div>
    </div>
    <div class="gap-2 mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
      <button
        type="button"
        class="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-dark sm:mt-0 sm:w-auto sm:text-sm"
        @click="handleModalResponse('no')">
        {{ $t("Cancel") }}
      </button>
      <button
        type="button"
        class="inline-flex justify-center w-full px-4 py-2 text-base font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
        @click="handleModalResponse('yes')">
        {{ $t("Yes") }}
      </button>
    </div>
  </Modal>
</template>

<script>
import TripleMenu from "@/components/shared/TripleMenu.vue"
import PageView from "@/components/shared/pages/PageView.vue"
import LayoutContainer from "@/components/shared/pages/LayoutContainer.vue"
import ModuleIcon from "@/components/shared/icons/ModuleIcon.vue"
import { ClipboardList, Mail, Folder, TrendingUp, MessagesSquare, List, CircleHelp, ChartNoAxesColumnDecreasing } from "lucide-vue-next"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import { PAGE_REFERENCES, PAGE_REFERENCES_MODULES } from "@/store/constants/references"
import Modal from "@/components/shared/Modal.vue"

export default {
  components: {
    Modal,
    CircleHelp,
    PageView,
    LayoutContainer,
    TripleMenu
  },
  inject: ["isModuleActivated", "getModule"],

  async beforeRouteLeave(to, from, next) {
    if (this?.edit_mode_active) {
      const ignore_changes = await this.ignoreLayoutChanges()
      this.edit_mode_active = ignore_changes != "yes"
      if (ignore_changes == "yes") {
        next()
      } else {
        next(false)
      }
    } else {
      next(true)
    }
  },
  data() {
    return {
      PAGE_REFERENCES,
      current_template: null,
      default_object_type_obj: { type: "default" },
      show_confirm_unsave_layout: false,
      ignore_changes: false,
      edit_mode_active: false,
      page_module: null
    }
  },
  computed: {
    top_right_menu_quick_actions() {
      return [
        {
          name: this.$t("Create a Group"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.CREATE_NEW_GROUP)
          },
          disabled: false,
          icon: Folder
        },
        {
          name: this.$t("Create a Plan"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.CREATE_PLAN)
          },
          disabled: false,
          icon: List
        },
        {
          name: this.$t("Create a Goal"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.CREATE_GOAL)
          },
          disabled: false,
          icon: TrendingUp
        },
        {
          name: this.$t("Create a Task"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.CREATE_TASK)
          },
          disabled: false,
          icon: ClipboardList
        },
        {
          name: this.$t("Create an Interaction"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.CREATE_INTERACTION)
          },
          disabled: false,
          icon: MessagesSquare
        },
        {
          name: this.$t("Send Email"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.SEND_EMAIL)
          },
          disabled: false,
          icon: Mail
        },
        {
          name: this.$t("Create a Custom Metric"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.CREATE_CUSTOM_METRIC, { view_type: "table" })
          },
          disabled: false,
          icon: ChartNoAxesColumnDecreasing
        }
      ]
    },
    top_right_menu_components_actions() {
      return [
        {
          name: this.$t("Edit Layout"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.EDIT_LAYOUT, {
              page_reference: PAGE_REFERENCES.HOMEPAGE,
              page_name: this.$route.params.page
            })
          },
          disabled: false,
          keyBoardShortcut: "I",
          icon: ModuleIcon
        },
        {
          name: this.$t("Add/Remove Components"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.ADD_NEW_COMPONENT, {
              page_reference: PAGE_REFERENCES.HOMEPAGE,
              current_template: this.current_template
            })
          },
          disabled: false,
          keyBoardShortcut: "U",
          icon: ModuleIcon
        },

        {
          name: this.$t("Share Page"),
          action: () => {
            this.sharePage()
          },
          disabled:
            this.$store.state.portfolio.shared_layouts.filter((l) => l.page == "dashboard").filter((l) => l.id == this.current_template?.id)
              .length != 0,
          icon: ModuleIcon
        },
        {
          name: this.$t("Share Page as a Template"),
          action: () => {
            this.clonePage()
          },
          disabled:
            this.$store.state.portfolio.shared_layouts.filter((l) => l.page == "dashboard").filter((l) => l.id == this.current_template?.id)
              .length != 0,
          icon: ModuleIcon
        },
        {
          name: this.current_template?.is_static ? this.$t("Reset Page") : this.$t("Delete Page"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.DELETE_PAGE, this.current_template)
          },
          icon: ModuleIcon
        }
      ]
    }
  },
  created() {
    this.getPageModule()
  },
  mounted() {
    this.show_confirm_unsave_layout = false
  },
  methods: {
    ignoreLayoutChanges() {
      return new Promise((resolve) => {
        this.show_confirm_unsave_layout = true
        // We Use a watcher to detect when the user has made a decision
        const unwatch = this.$watch("ignore_changes", (newValue) => {
          if (newValue != undefined) {
            this.show_confirm_unsave_layout = false
            // give the modal time to close
            setTimeout(() => {
              resolve(newValue)
              // Remove the watcher once the decision has been made
              unwatch()
              // Reset the value to undefined super important
              this.ignore_changes = undefined
            }, 300)
          }
        })
      })
    },
    handleModalResponse(answer) {
      this.ignore_changes = answer
    },
    getPageModule() {
      this.page_module = this.getModule(PAGE_REFERENCES_MODULES[PAGE_REFERENCES.HOMEPAGE])
      if (!this.page_module) {
        setTimeout(() => {
          this.getPageModule()
        }, 500)
      }
    },
    setEditMode(value) {
      this.edit_mode_active = value
    },

    sharePage() {
      this.$emitter.emit(ALL_EMITTERS.SHARE_PAGE, this.current_template)
    },
    clonePage() {
      this.$emitter.emit(ALL_EMITTERS.CLONE_PAGE, this.current_template)
    }
  }
}
</script>
